import type moment from 'moment'

import LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { SHIFT_CONFLICTS } from 'modules/shift_management/hooks/reducers/filter'
import { MAX_ITEMS_PER_PAGE } from 'modules/shift_management/hooks/reducers/pagination'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import Api from 'modules/shift_management/lib/Api'
import type { ShiftsRow, View } from 'modules/shift_management/lib/types'

import ShiftsConflicts from '../store/internal/collections/ShiftsConflicts'
import { useDefaultShifts } from './useDefaultShifts'
import { useEnabledFeatures } from './useEnabledFeatures'
import { useFilters } from './useFilters'
import { useOrderOptions } from './useOrderEmployees'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'
import { useTemplateWeeks } from './useTemplateWeeks'
import { useUI } from './useUI'
import { useUnassignedShifts } from './useUnassignedShifts'

const useLoadBootstrapData = (contractVersions: ContractVersions) => {
  const [filters] = useFilters()
  const { fetchEmployeesAndStats } = useFetchEmployeesAndStats(contractVersions)
  const { fetchStats } = useFetchStats()

  const loadBootstrapData = (targetView?: View) => {
    if (filters.status_id === SHIFT_CONFLICTS && !filters.shift_ids) {
      fetchStats(targetView)
    } else {
      fetchEmployeesAndStats(targetView)
    }
  }

  return { loadBootstrapData }
}

const useFetchEmployeesAndStats = (contractVersions: ContractVersions) => {
  const [filters] = useFilters()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [features] = useEnabledFeatures()
  const [, dispatchPagination] = usePagination()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchTemplateWeeks] = useTemplateWeeks()
  const [, dispatchDefaultShifts] = useDefaultShifts()
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const [, dispatchUI] = useUI()
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()

  const fetchEmployeesAndStats = async (targetView?: View) => {
    const page = filters.status_id === SHIFT_CONFLICTS ? null : 1
    const { startAt, endAt, groupType } = targetView || view
    const [
      { shiftsRows, leaves, holidays, restDays, shifts },
      { stats },
      defaultShifts,
      templateWeeks,
      unassignedShifts,
    ] = await Promise.all([
      Api.fetch(
        {
          page,
          startAt,
          endAt,
          filters,
          groupType,
          features,
          orderBy: localStorageOrderEmployeesBy,
          orderDir: localStorageOrderEmployeesDir,
        },
        contractVersions
      ),
      Api.fetchStats({ startAt, endAt, filters, view }),
      Api.fetchDefaultShifts(),
      Api.fetchTemplateWeeks(),
      Api.fetchUnassignedShifts({ features, startAt, endAt, view }),
    ])

    const statsResult = {
      hasDrafts: stats.has_drafts,
      hasDraftsWithoutConflict: stats.has_drafts_without_conflict,
      shiftConflicts: ShiftsConflicts.buildFromProps(stats?.shift_conflicts || []),
    }

    dispatchSchedule({
      type: 'BOOTSTRAP',
      stats: statsResult,
      leaves,
      holidays,
      restDays,
      shifts,
    })
    dispatchPagination({ type: 'CHECK_LAST_PAGE', shiftsRows, view })
    dispatchShiftsRows({ type: 'BOOTSTRAP', shiftsRows })
    dispatchTemplateWeeks({ type: 'BOOTSTRAP', templateWeeks })
    dispatchDefaultShifts({ type: 'BOOTSTRAP', defaultShifts })
    dispatchUnassignedShifts({ type: 'BOOTSTRAP', unassignedShifts })
    dispatchUI({ type: 'NOT_LOADING' })
  }

  return { fetchEmployeesAndStats }
}

const useFetchStats = () => {
  const [filters, dispatchFilters] = useFilters()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchTemplateWeeks] = useTemplateWeeks()
  const [, dispatchDefaultShifts] = useDefaultShifts()
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const [, dispatchUI] = useUI()

  const fetchStats = async (targetView?: View) => {
    const { startAt, endAt } = targetView || view
    const { stats } = await Api.fetchStats({ startAt, endAt, filters, view })
    const shiftIds = stats?.shift_conflicts?.map((s) => s.id)
    const statsResult = {
      hasDrafts: stats.has_drafts,
      hasDraftsWithoutConflict: stats.has_drafts_without_conflict,
      shiftConflicts: ShiftsConflicts.buildFromProps(stats?.shift_conflicts || []),
    }

    dispatchSchedule({
      type: 'BOOTSTRAP',
      stats: statsResult,
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shifts: undefined,
    })
    dispatchTemplateWeeks({ type: 'BOOTSTRAP', templateWeeks: undefined })
    dispatchDefaultShifts({ type: 'BOOTSTRAP', defaultShifts: undefined })
    dispatchUnassignedShifts({ type: 'BOOTSTRAP', unassignedShifts: undefined })

    dispatchUI({ type: 'LOADING' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
    dispatchFilters({
      type: 'FILTER_CUSTOM',
      id: filters.status_id,
      employeeIds: filters.employee_ids,
      customFilters: filters.custom_filters,
      logicOperator: new LogicOperatorForm({ logic_operator: filters.logic_operator }),
      shiftIds: shiftIds,
      withoutShiftsIds: null,
      terminated: filters.terminated,
    })
  }

  return { fetchStats }
}

const useLoadPage = (contractVersions: ContractVersions) => {
  const [filters] = useFilters()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [features] = useEnabledFeatures()
  const [{ page }, dispatchPagination] = usePagination()
  const [, dispatchShiftsRows] = useShiftsRows()
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()

  const loadPage = async () => {
    const { startAt, endAt, groupType } = view
    const { shiftsRows } = await Api.fetch(
      {
        page,
        startAt,
        endAt,
        filters,
        groupType,
        features,
        orderBy: localStorageOrderEmployeesBy,
        orderDir: localStorageOrderEmployeesDir,
      },
      contractVersions
    )

    dispatchSchedule({ type: 'ADD_SHIFTS_ROWS', shiftsRows })
    dispatchPagination({ type: 'CHECK_LAST_PAGE', shiftsRows, view })
    dispatchShiftsRows({ type: 'ADD_SHIFTS_ROWS', shiftsRows })
  }

  return { loadPage }
}

const useFetchEmployees = (contractVersions: ContractVersions) => {
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const [features] = useEnabledFeatures()
  const [{ page, hasMore }] = usePagination()

  const { loadBootstrapData } = useLoadBootstrapData(contractVersions)
  const { loadPage } = useLoadPage(contractVersions)

  const [{ shiftsRows }] = useShiftsRows()
  useUnsafeEffect(() => {
    if (!hasMore) return

    if (page === 1) {
      loadBootstrapData()
    } else {
      loadPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, view, filters, hasMore, features])

  useUnsafeEffect(() => {
    trackEvent(view, shiftsRows)
  }, [shiftsRows, view])
}

const trackEvent = (view: View, shiftsRows: ShiftsRow[]) => {
  const { length } = shiftsRows
  if (length <= MAX_ITEMS_PER_PAGE || !view.isByEmployees) return

  track(trackingEvents.shiftManagement.requestShiftManagementEmployees, {
    view: view.viewType,
    view_type_by: view.groupType,
    // As this event is triggered only if the page is not 1, we are safe,
    // but we should consider to change it if we enable pagination for the new areas view, role view, etc
    number_of_employees_in_view: shiftsRows.length,
  })
}

const useLoadAllEmployees = (contractVersions: ContractVersions) => {
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const [features] = useEnabledFeatures()
  const dispatchSelection = useSelectionDispatch()
  const loadAllEmployees = async (
    hasMore: boolean,
    allEmployeesSelected: boolean,
    loadedShiftsRows: ShiftsRow[]
  ) => {
    if (allEmployeesSelected) {
      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })
    } else {
      const { startAt, endAt, groupType } = view
      // Perform extra request if we have more items and only for employees view
      const { shiftsRows } =
        hasMore && view.isByEmployees
          ? await Api.fetch({ startAt, endAt, filters, groupType, features }, contractVersions)
          : { shiftsRows: loadedShiftsRows }

      dispatchSelection({
        type: 'TOGGLE_ALL_SHIFTS_ROWS',
        shiftsRows,
        view,
      })
    }
  }

  return { loadAllEmployees }
}

const useLoadColumnEmployees = (contractVersions: ContractVersions) => {
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const [features] = useEnabledFeatures()

  const loadColumnEmployees = async (
    hasMore: boolean,
    loadedShiftsRows: ShiftsRow[],
    day: moment.Moment
  ) => {
    const dayId = dateToString(day, 'backend')

    const { shiftsRows } =
      hasMore && view.isByEmployees
        ? await Api.fetchDay(dayId, filters, features, contractVersions)
        : { shiftsRows: loadedShiftsRows }

    dispatchSelection({
      type: 'TOGGLE_DAY_COLUMN',
      shiftsRows,
      day,
      view,
    })
  }

  return { loadColumnEmployees }
}

export { useLoadAllEmployees, useLoadBootstrapData, useLoadColumnEmployees }

export default useFetchEmployees
